import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Copy,
  CheckCircle,
  ExternalLink,
  Globe,
  Key
} from "lucide-react";
import Layout from "@/components/Layout";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CLIP_FORMATS } from '@/lib/projects/clipFormats';

const CLIP_FORMAT_LIST = CLIP_FORMATS.map((format) => `'${format}'`).join(', ');

const ApiReference = () => {
  const { t } = useTranslation();
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const endpoints = [
    {
      id: "create-project",
      method: "POST",
      path: "/api/v1/project",
      summary: t('apiReference.endpoints.createProject.summary'),
      description: t('apiReference.endpoints.createProject.description'),
      request: {
        contentType: "application/json",
        schema: {
          base_video_url: { type: "string", required: true, description: "URL of the video to process (YouTube, Vimeo, or direct link)" },
          webhook_url: { type: "string", required: false, description: "URL to receive completion notifications" },
          name: { type: "string", required: false, description: "Human-readable name for the project" },
          format: { type: "string", required: false, description: `Output format: ${CLIP_FORMAT_LIST}` },
          language: { type: "string", required: false, description: "Language code (e.g., 'en-US', 'pt-BR')" },
          prompt: { type: "string", required: false, description: "Additional guidance for clip generation" },
          brand_kit_id: { type: "string", required: false, description: "Template ID to apply subtitle styles, logo, and colors. Fallback order: provided ID → account default template → public 'Basic' template." }
        }
      },
      responses: {
        "200": t('apiReference.endpoints.createProject.response200'),
        "400": t('apiReference.endpoints.createProject.response400'),
        "401": t('apiReference.endpoints.createProject.response401'),
      },
      example: {
        request: `{
  "base_video_url": "https://www.youtube.com/watch?v=W7dB7uJOD-k",
  "webhook_url": "https://webhook.site/abc123",
  "name": "My Project",
  "format": "auto",
  "language": "pt-BR",
  "prompt": "Focus on main topics",
  "brand_kit_id": "xx7467jzxygqwslquyuwz142"
}`,
        response: `{
  "id": "mdzq28deb1aghlrnvz1rmrcz",
  "name": "My Project",
  "format": "auto",
  "language": "pt-BR",
  "prompt": "Focus on main topics",
  "base_video_url": "https://www.youtube.com/watch?v=W7dB7uJOD-k",
  "webhook_url": "https://webhook.site/abc123",
  "process_status": "processing",
  "thumbnail_url": null,
  "clips": [],
  "createdAt": "2025-08-22T15:19:38.034Z",
  "updatedAt": "2025-08-22T15:19:38.034Z"
}`,
        curl: `curl --request POST \\
  --url https://api.autoclipper.live/api/v1/project \\
  --header 'Authorization: API-Key YOUR_API_KEY' \\
  --header 'Content-Type: application/json' \\
  --data '{
    "base_video_url": "https://www.youtube.com/watch?v=W7dB7uJOD-k",
    "webhook_url": "https://webhook.site/abc123",
    "name": "My Project",
    "format": "auto",
    "language": "pt-BR",
    "prompt": "Focus on main topics",
    "brand_kit_id": "xx7467jzxygqwslquyuwz142"
  }'`
      }
    },
    {
      id: "list-projects",
      method: "GET",
      path: "/api/v1/projects",
      summary: t('apiReference.endpoints.listProjects.summary'),
      description: t('apiReference.endpoints.listProjects.description'),
      request: {
        parameters: {
          limit: { type: "integer", required: false, description: "Maximum number of projects to return (1-100, default: 20)" },
          offset: { type: "integer", required: false, description: "Number of projects to skip for pagination (default: 0)" }
        }
      },
      responses: {
        "200": t('apiReference.endpoints.listProjects.response200'),
        "401": t('apiReference.endpoints.listProjects.response401'),
      },
      example: {
        curl: `curl --request GET \\
  --url 'https://api.autoclipper.live/api/v1/projects?limit=5&offset=0' \\
  --header 'Authorization: API-Key YOUR_API_KEY'`,
        response: `[
  {
    "id": "mdzq28deb1aghlrnvz1rmrcz",
    "name": "My Project",
    "format": "auto",
    "language": "pt-BR",
    "process_status": "completed",
    "thumbnail_url": "https://storage.example.com/thumbnail.jpg",
    "clips": [...],
    "createdAt": "2025-08-22T15:19:38.034Z",
    "updatedAt": "2025-08-22T15:22:31.461Z"
  }
]`
      }
    },
    {
      id: "get-project",
      method: "GET",
      path: "/api/v1/project/{id}",
      summary: t('apiReference.endpoints.getProject.summary'),
      description: t('apiReference.endpoints.getProject.description'),
      request: {
        parameters: {
          id: { type: "string", required: true, description: "The unique identifier of the project" }
        }
      },
      responses: {
        "200": t('apiReference.endpoints.getProject.response200'),
        "401": t('apiReference.endpoints.getProject.response401'),
        "404": t('apiReference.endpoints.getProject.response404'),
      },
      example: {
        curl: `curl --request GET \\
  --url https://api.autoclipper.live/api/v1/project/mdzq28deb1aghlrnvz1rmrcz \\
  --header 'Authorization: API-Key YOUR_API_KEY'`,
        response: `{
  "id": "mdzq28deb1aghlrnvz1rmrcz",
  "name": "My Project",
  "format": "auto",
  "language": "pt-BR",
  "process_status": "completed",
  "base_video_url": "https://www.youtube.com/watch?v=W7dB7uJOD-k",
  "webhook_url": "https://webhook.site/abc123",
  "thumbnail_url": "https://storage.example.com/thumbnail.jpg",
  "clips": [
    {
      "id": "delma233vetntmlkkr02pg5c",
      "name": "Main Topic Highlight",
      "rendered_video_url": "https://storage.example.com/final-clip.mp4"
    }
  ],
  "createdAt": "2025-08-22T15:19:38.034Z",
  "updatedAt": "2025-08-22T15:22:31.461Z"
}`
      }
    },
    {
      id: "list-community-libraries",
      method: "GET",
      path: "/api/v1/community/libraries",
      summary: t('apiReference.endpoints.listCommunityLibraries.summary'),
      description: t('apiReference.endpoints.listCommunityLibraries.description'),
      request: {
        parameters: {
          limit: { type: "integer", required: false, description: "Maximum number of libraries to return (1-100, default 30)" },
          offset: { type: "integer", required: false, description: "Pagination offset (default 0)" },
          search: { type: "string", required: false, description: "Case-insensitive substring match on title (and channel name when channel_id not set)" },
          channel_id: { type: "string", required: false, description: "Filter to libraries from this channel id (from GET /v1/community/channels)" },
          channel_category: { type: "string", required: false, description: "Filter to channels with this category" },
          tag_id: { type: "string", required: false, description: "Filter to libraries tagged with this tag id" },
          sort: { type: "string", required: false, description: "newest | oldest | views | subscribers | remixes (default newest)" },
        },
      },
      responses: {
        "200": t('apiReference.endpoints.listCommunityLibraries.response200'),
        "401": t('apiReference.endpoints.listCommunityLibraries.response401'),
      },
      example: {
        curl: `curl --request GET \\
  --url 'https://api.autoclipper.live/api/v1/community/libraries?limit=10&sort=remixes' \\
  --header 'Authorization: API-Key YOUR_API_KEY'`,
        response: `[
  {
    "id": "mdzq28deb1aghlrnvz1rmrcz",
    "name": "Why Marketing Funnels Still Work in 2025",
    "video_url": "https://www.youtube.com/watch?v=abc123",
    "thumbnail_url": "https://i.ytimg.com/vi/abc123/maxresdefault.jpg",
    "duration_seconds": 1842.5,
    "view_count": 154820,
    "upload_date": "20250131",
    "language": "en-US",
    "format": "auto",
    "youtube_video_id": "abc123",
    "provider": "youtube",
    "channel": {
      "id": "ch_doc_id",
      "channel_id": "UCabc123",
      "name": "Marketing School",
      "subscriber_count": 980000,
      "category": "business"
    },
    "tags": [{ "id": "t1", "name": "marketing", "color": "#22d3ee" }],
    "remix_count": 12,
    "createdAt": "2025-01-31T10:00:00.000Z",
    "updatedAt": "2025-01-31T10:05:00.000Z"
  }
]`,
      },
    },
    {
      id: "get-community-library",
      method: "GET",
      path: "/api/v1/community/library/{id}",
      summary: t('apiReference.endpoints.getCommunityLibrary.summary'),
      description: t('apiReference.endpoints.getCommunityLibrary.description'),
      request: {
        parameters: {
          id: { type: "string", required: true, description: "Community library id" },
        },
      },
      responses: {
        "200": t('apiReference.endpoints.getCommunityLibrary.response200'),
        "401": t('apiReference.endpoints.getCommunityLibrary.response401'),
        "404": t('apiReference.endpoints.getCommunityLibrary.response404'),
      },
      example: {
        curl: `curl --request GET \\
  --url https://api.autoclipper.live/api/v1/community/library/mdzq28deb1aghlrnvz1rmrcz \\
  --header 'Authorization: API-Key YOUR_API_KEY'`,
      },
    },
    {
      id: "list-community-channels",
      method: "GET",
      path: "/api/v1/community/channels",
      summary: t('apiReference.endpoints.listCommunityChannels.summary'),
      description: t('apiReference.endpoints.listCommunityChannels.description'),
      request: {
        parameters: {
          search: { type: "string", required: false, description: "Case-insensitive substring match on channel name" },
          category: { type: "string", required: false, description: "Filter channels by category" },
          sort: { type: "string", required: false, description: "name | remixes | subscribers (default name)" },
        },
      },
      responses: {
        "200": t('apiReference.endpoints.listCommunityChannels.response200'),
        "401": t('apiReference.endpoints.listCommunityChannels.response401'),
      },
      example: {
        curl: `curl --request GET \\
  --url 'https://api.autoclipper.live/api/v1/community/channels?sort=remixes' \\
  --header 'Authorization: API-Key YOUR_API_KEY'`,
      },
    },
    {
      id: "get-community-channel",
      method: "GET",
      path: "/api/v1/community/channel/{id}",
      summary: t('apiReference.endpoints.getCommunityChannel.summary'),
      description: t('apiReference.endpoints.getCommunityChannel.description'),
      request: {
        parameters: {
          id: { type: "string", required: true, description: "Channel documentId" },
        },
      },
      responses: {
        "200": t('apiReference.endpoints.getCommunityChannel.response200'),
        "401": t('apiReference.endpoints.getCommunityChannel.response401'),
        "404": t('apiReference.endpoints.getCommunityChannel.response404'),
      },
      example: {
        curl: `curl --request GET \\
  --url https://api.autoclipper.live/api/v1/community/channel/ch_doc_id \\
  --header 'Authorization: API-Key YOUR_API_KEY'`,
      },
    },
    {
      id: "list-community-channel-libraries",
      method: "GET",
      path: "/api/v1/community/channel/{id}/libraries",
      summary: t('apiReference.endpoints.listCommunityChannelLibraries.summary'),
      description: t('apiReference.endpoints.listCommunityChannelLibraries.description'),
      request: {
        parameters: {
          id: { type: "string", required: true, description: "Channel documentId" },
          limit: { type: "integer", required: false, description: "1-100, default 30" },
          offset: { type: "integer", required: false, description: "default 0" },
          search: { type: "string", required: false, description: "Substring match on title" },
          tag_id: { type: "string", required: false, description: "Filter to videos tagged with this tag id" },
          sort: { type: "string", required: false, description: "newest | oldest | views | subscribers | remixes (default newest)" },
        },
      },
      responses: {
        "200": t('apiReference.endpoints.listCommunityChannelLibraries.response200'),
        "401": t('apiReference.endpoints.listCommunityChannelLibraries.response401'),
        "404": t('apiReference.endpoints.listCommunityChannelLibraries.response404'),
      },
      example: {
        curl: `curl --request GET \\
  --url 'https://api.autoclipper.live/api/v1/community/channel/ch_doc_id/libraries?sort=views&limit=20' \\
  --header 'Authorization: API-Key YOUR_API_KEY'`,
      },
    },
    {
      id: "check-community-library",
      method: "GET",
      path: "/api/v1/community/check",
      summary: t('apiReference.endpoints.checkCommunityLibrary.summary'),
      description: t('apiReference.endpoints.checkCommunityLibrary.description'),
      request: {
        parameters: {
          url: { type: "string", required: true, description: "Video URL (YouTube, Vimeo, etc.) to look up" },
        },
      },
      responses: {
        "200": t('apiReference.endpoints.checkCommunityLibrary.response200'),
        "400": t('apiReference.endpoints.checkCommunityLibrary.response400'),
        "401": t('apiReference.endpoints.checkCommunityLibrary.response401'),
        "404": t('apiReference.endpoints.checkCommunityLibrary.response404'),
      },
      example: {
        curl: `curl --request GET \\
  --url 'https://api.autoclipper.live/api/v1/community/check?url=https://www.youtube.com/watch?v=abc123' \\
  --header 'Authorization: API-Key YOUR_API_KEY'`,
      },
    },
    {
      id: "list-community-tags",
      method: "GET",
      path: "/api/v1/community/tags",
      summary: t('apiReference.endpoints.listCommunityTags.summary'),
      description: t('apiReference.endpoints.listCommunityTags.description'),
      request: {
        parameters: {
          channel_id: { type: "string", required: false, description: "Restrict tag counts to one channel" },
          channel_category: { type: "string", required: false, description: "Restrict tag counts to a channel category" },
        },
      },
      responses: {
        "200": t('apiReference.endpoints.listCommunityTags.response200'),
        "401": t('apiReference.endpoints.listCommunityTags.response401'),
      },
      example: {
        curl: `curl --request GET \\
  --url https://api.autoclipper.live/api/v1/community/tags \\
  --header 'Authorization: API-Key YOUR_API_KEY'`,
      },
    },
    {
      id: "remix-community-library",
      method: "POST",
      path: "/api/v1/community/library/{id}/remix",
      summary: t('apiReference.endpoints.remixCommunityLibrary.summary'),
      description: t('apiReference.endpoints.remixCommunityLibrary.description'),
      request: {
        contentType: "application/json",
        schema: {
          webhook_url: { type: "string", required: false, description: "Optional webhook URL for completion notifications" },
          name: { type: "string", required: false, description: "Project name override (defaults to source library title)" },
          format: { type: "string", required: false, description: "Format override (defaults to source library format)" },
          language: { type: "string", required: false, description: "Language override" },
          prompt: { type: "string", required: false, description: "Prompt override" },
          brand_kit_id: { type: "string", required: false, description: "Template id to apply" },
          auto_export: { type: "boolean", required: false, description: "Render clips after curation. Defaults to false for remixes." },
        },
      },
      responses: {
        "200": t('apiReference.endpoints.remixCommunityLibrary.response200'),
        "401": t('apiReference.endpoints.remixCommunityLibrary.response401'),
        "404": t('apiReference.endpoints.remixCommunityLibrary.response404'),
        "422": t('apiReference.endpoints.remixCommunityLibrary.response422'),
      },
      example: {
        request: `{
  "webhook_url": "https://webhook.site/abc123",
  "auto_export": true
}`,
        curl: `curl --request POST \\
  --url https://api.autoclipper.live/api/v1/community/library/mdzq28deb1aghlrnvz1rmrcz/remix \\
  --header 'Authorization: API-Key YOUR_API_KEY' \\
  --header 'Content-Type: application/json' \\
  --data '{ "webhook_url": "https://webhook.site/abc123", "auto_export": true }'`,
      },
    },
    {
      id: "create-project-clip",
      method: "POST",
      path: "/api/v1/project/{id}/clip",
      summary: t('apiReference.endpoints.createProjectClip.summary'),
      description: t('apiReference.endpoints.createProjectClip.description'),
      request: {
        contentType: "application/json",
        schema: {
          start_time: { type: "number", required: true, description: "Clip start time in seconds (relative to source video)" },
          end_time: { type: "number", required: true, description: "Clip end time in seconds; must be greater than start_time" },
          name: { type: "string", required: false, description: "Optional clip name" },
          brand_kit_id: { type: "string", required: false, description: "Template id; defaults to the project's template" },
          auto_render: { type: "boolean", required: false, description: "Render immediately after creation (default true). When false, returns a clip with render_status: 'not_rendered' that you can render later via POST /v1/clip/{id}/render." },
        },
      },
      responses: {
        "201": t('apiReference.endpoints.createProjectClip.response201'),
        "400": t('apiReference.endpoints.createProjectClip.response400'),
        "401": t('apiReference.endpoints.createProjectClip.response401'),
        "402": t('apiReference.endpoints.createProjectClip.response402'),
        "404": t('apiReference.endpoints.createProjectClip.response404'),
        "409": t('apiReference.endpoints.createProjectClip.response409'),
      },
      example: {
        request: `{
  "start_time": 124.3,
  "end_time": 167.8,
  "name": "Best part",
  "auto_render": true
}`,
        curl: `curl --request POST \\
  --url https://api.autoclipper.live/api/v1/project/mdzq28deb1aghlrnvz1rmrcz/clip \\
  --header 'Authorization: API-Key YOUR_API_KEY' \\
  --header 'Content-Type: application/json' \\
  --data '{ "start_time": 124.3, "end_time": 167.8, "name": "Best part" }'`,
        response: `{
  "id": "delma233vetntmlkkr02pg5c",
  "name": "Best part",
  "start_time": 124.3,
  "end_time": 167.8,
  "duration_seconds": 43.5,
  "rendered_video_url": null,
  "render_status": "processing",
  "createdAt": "2026-05-15T12:00:00.000Z",
  "updatedAt": "2026-05-15T12:00:00.000Z"
}`,
      },
    },
    {
      id: "render-clip",
      method: "POST",
      path: "/api/v1/clip/{id}/render",
      summary: t('apiReference.endpoints.renderClip.summary'),
      description: t('apiReference.endpoints.renderClip.description'),
      request: {
        contentType: "application/json",
        schema: {
          brand_kit_id: { type: "string", required: false, description: "Override template for this render" },
        },
      },
      responses: {
        "200": t('apiReference.endpoints.renderClip.response200'),
        "400": t('apiReference.endpoints.renderClip.response400'),
        "401": t('apiReference.endpoints.renderClip.response401'),
        "402": t('apiReference.endpoints.renderClip.response402'),
        "404": t('apiReference.endpoints.renderClip.response404'),
      },
      example: {
        request: `{ "brand_kit_id": "xx7467jzxygqwslquyuwz142" }`,
        curl: `curl --request POST \\
  --url https://api.autoclipper.live/api/v1/clip/delma233vetntmlkkr02pg5c/render \\
  --header 'Authorization: API-Key YOUR_API_KEY' \\
  --header 'Content-Type: application/json' \\
  --data '{}'`,
        response: `{
  "id": "delma233vetntmlkkr02pg5c",
  "name": "Best part",
  "start_time": 124.3,
  "end_time": 167.8,
  "duration_seconds": 43.5,
  "rendered_video_url": null,
  "render_status": "processing",
  "createdAt": "2026-05-15T12:00:00.000Z",
  "updatedAt": "2026-05-15T12:01:30.000Z"
}`,
      },
    },
  ];

  const schemas = [
    {
      name: "Project",
      description: "Represents a video processing project",
      properties: {
        id: { type: "string", description: "Unique identifier for the project" },
        name: { type: "string", description: "Human-readable name for the project" },
        format: { type: "string", description: `Output format: ${CLIP_FORMAT_LIST}` },
        language: { type: "string", description: "Language code for video processing" },
        prompt: { type: "string", description: "Prompt used to guide clip generation" },
        base_video_url: { type: "string", description: "URL of the original video" },
        webhook_url: { type: "string", description: "Webhook URL for notifications (nullable)" },
        process_status: { type: "string", description: "Current status: 'processing', 'completed', 'failed'" },
        auto_export: { type: "boolean", description: "Whether clips are auto-rendered after curation (default true for /v1/project, false for /remix)" },
        thumbnail_url: { type: "string", description: "URL of the project thumbnail (nullable)" },
        clips: { type: "array", description: "Array of generated clips" },
        createdAt: { type: "string", description: "ISO timestamp when project was created" },
        updatedAt: { type: "string", description: "ISO timestamp when project was last updated" }
      }
    },
    {
      name: "Clip",
      description: "Represents a generated video clip",
      properties: {
        id: { type: "string", description: "Unique identifier for the clip" },
        name: { type: "string", description: "Generated title for the clip (nullable)" },
        start_time: { type: "number", description: "Start of the curated segment in seconds (nullable)" },
        end_time: { type: "number", description: "End of the curated segment in seconds (nullable)" },
        duration_seconds: { type: "number", description: "Clip duration in seconds (nullable)" },
        rendered_video_url: { type: "string", description: "URL of the final rendered video. Null until rendered or when auto_export was false." },
        render_status: { type: "string", description: "Lifecycle: 'processing', 'completed', 'failed', 'expired', or 'not_rendered' (curated but not queued for render). Returned by clip endpoints, not by the project endpoints." },
      }
    },
    {
      name: "CommunityLibrary",
      description: "Represents a community-curated source video that can be remixed",
      properties: {
        id: { type: "string", description: "Library id (use with /community/library/{id}/remix)" },
        name: { type: "string", description: "Library title (usually the source video's title)" },
        video_url: { type: "string", description: "Canonical source URL (e.g. YouTube). Pass back to /v1/project as base_video_url." },
        thumbnail_url: { type: "string", description: "Thumbnail (nullable)" },
        duration_seconds: { type: "number", description: "Source video duration in seconds" },
        view_count: { type: "integer", description: "Source video view count at download time" },
        upload_date: { type: "string", description: "Provider-supplied upload date (e.g. YYYYMMDD)" },
        language: { type: "string", description: "Curation language" },
        format: { type: "string", description: `Curation format (${CLIP_FORMAT_LIST})` },
        youtube_video_id: { type: "string", description: "Provider video id (nullable)" },
        provider: { type: "string", description: "Provider tag, e.g. 'youtube'" },
        channel: { type: "object", description: "Channel summary (nullable)" },
        tags: { type: "array", description: "Array of tag objects" },
        remix_count: { type: "integer", description: "Number of remixes across all accounts" },
        createdAt: { type: "string", description: "ISO timestamp" },
        updatedAt: { type: "string", description: "ISO timestamp" },
      }
    },
  ];

  const webhookSuccessPayload = `{
  "id": "mdzq28deb1aghlrnvz1rmrcz",
  "name": "Projeto 01",
  "format": "auto",
  "language": "pt-BR",
  "prompt": "",
  "base_video_url": "https://www.youtube.com/watch?v=W7dB7uJOD-k",
  "webhook_url": "https://webhook.site/463a87db-9415-41bb-862e-eba1063a88d8",
  "process_status": "completed",
  "thumbnail_url": "https://storage.example.com/thumbnail.jpg",
  "clips": [
    {
      "id": "delma233vetntmlkkr02pg5c",
      "name": "Main Topic Highlight",
      "rendered_video_url": "https://storage.example.com/final-clip-1.mp4"
    },
    {
      "id": "p4au418clxdg54gpag9sm37n",
      "name": "Key Moment Deep Dive",
      "rendered_video_url": "https://storage.example.com/final-clip-2.mp4"
    }
  ],
  "createdAt": "2025-08-22T15:19:38.034Z",
  "updatedAt": "2025-08-22T15:22:31.461Z"
}`;

  const webhookFailurePayload = `{
  "id": "mdzq28deb1aghlrnvz1rmrcz",
  "name": "Projeto 01",
  "format": "auto",
  "language": "pt-BR",
  "prompt": "",
  "base_video_url": "https://www.youtube.com/watch?v=W7dB7uJOD-k",
  "webhook_url": "https://webhook.site/463a87db-9415-41bb-862e-eba1063a88d8",
  "process_status": "failed",
  "thumbnail_url": null,
  "clips": [],
  "createdAt": "2025-08-22T15:19:38.034Z",
  "updatedAt": "2025-08-22T15:22:31.461Z"
}`;

  return (
    <Layout>
      <div className="container mx-auto p-6 space-y-8">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button asChild variant="ghost" size="sm">
            <Link to="/api-documentation">
              <ArrowLeft className="w-4 h-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold">{t('apiReference.title')}</h1>
            <p className="text-muted-foreground">
              {t('apiReference.subtitle')}
            </p>
          </div>
        </div>

        {/* Quick Links */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5" />
              {t('apiReference.baseUrlAuth')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-semibold text-sm mb-2">{t('apiReference.baseUrl')}</h4>
              <code className="bg-muted px-3 py-2 rounded-lg block">https://api.autoclipper.live</code>
            </div>

            <div>
              <h4 className="font-semibold text-sm mb-2">{t('apiReference.authentication')}</h4>
              <p className="text-sm text-muted-foreground mb-2">
                {t('apiReference.authDescription')}
              </p>
              <code className="bg-muted px-3 py-2 rounded-lg block">Authorization: API-Key YOUR_API_KEY</code>
            </div>

            <div className="flex gap-2">
              <Button asChild variant="outline" size="sm">
                <Link to="/api-keys">
                  <Key className="w-4 h-4 mr-2" />
                  {t('apiReference.getApiKey')}
                </Link>
              </Button>
              <Button asChild variant="outline" size="sm">
                <a href="/openapi.json" target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  {t('apiReference.openApiSpec')}
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Endpoints */}
        <div className="space-y-8">
          {endpoints.map((endpoint) => (
            <Card key={endpoint.id} id={endpoint.id}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Badge variant={endpoint.method === 'GET' ? 'secondary' : 'default'}>
                    {endpoint.method}
                  </Badge>
                  <code className="text-lg">{endpoint.path}</code>
                </CardTitle>
                <CardDescription>{endpoint.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="overview" className="space-y-4">
                  <TabsList>
                    <TabsTrigger value="overview">{t('apiReference.tabs.overview')}</TabsTrigger>
                    <TabsTrigger value="parameters">{t('apiReference.tabs.parameters')}</TabsTrigger>
                    <TabsTrigger value="examples">{t('apiReference.tabs.examples')}</TabsTrigger>
                  </TabsList>

                  <TabsContent value="overview" className="space-y-4">
                    <div>
                      <h4 className="font-semibold mb-2">{t('apiReference.summary')}</h4>
                      <p className="text-sm text-muted-foreground">{endpoint.summary}</p>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">{t('apiReference.responseCodes')}</h4>
                      <div className="space-y-1">
                        {Object.entries(endpoint.responses).map(([code, description]) => (
                          <div key={code} className="flex gap-2 text-sm">
                            <Badge variant={code === '200' ? 'default' : 'secondary'}>
                              {code}
                            </Badge>
                            <span className="text-muted-foreground">{description}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="parameters" className="space-y-4">
                    {endpoint.request?.schema && (
                      <div>
                        <h4 className="font-semibold mb-2">{t('apiReference.requestBodyTitle')}</h4>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>{t('apiReference.parameter')}</TableHead>
                              <TableHead>{t('apiReference.type')}</TableHead>
                              <TableHead>{t('apiReference.required')}</TableHead>
                              <TableHead>{t('apiReference.description')}</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {Object.entries(endpoint.request.schema).map(([key, value]) => (
                              <TableRow key={key}>
                                <TableCell><code>{key}</code></TableCell>
                                <TableCell><Badge variant="outline">{value.type}</Badge></TableCell>
                                <TableCell>
                                  <Badge variant={value.required ? 'default' : 'secondary'}>
                                    {value.required ? t('apiReference.yes') : t('apiReference.no')}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-sm">{value.description}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}

                    {endpoint.request?.parameters && (
                      <div>
                        <h4 className="font-semibold mb-2">{t('apiReference.queryParameters')}</h4>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>{t('apiReference.parameter')}</TableHead>
                              <TableHead>{t('apiReference.type')}</TableHead>
                              <TableHead>{t('apiReference.required')}</TableHead>
                              <TableHead>{t('apiReference.description')}</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {Object.entries(endpoint.request.parameters).map(([key, value]) => (
                              <TableRow key={key}>
                                <TableCell><code>{key}</code></TableCell>
                                <TableCell><Badge variant="outline">{value.type}</Badge></TableCell>
                                <TableCell>
                                  <Badge variant={value.required ? 'default' : 'secondary'}>
                                    {value.required ? t('apiReference.yes') : t('apiReference.no')}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-sm">{value.description}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="examples" className="space-y-4">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <h4 className="font-semibold">{t('apiReference.curlRequest')}</h4>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(endpoint.example.curl, `${endpoint.id}-curl`)}
                        >
                          {copiedCode === `${endpoint.id}-curl` ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        </Button>
                      </div>
                      <pre className="bg-muted p-3 rounded-lg text-sm overflow-x-auto">
                        <code>{endpoint.example.curl}</code>
                      </pre>
                    </div>

                    {endpoint.example.request && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <h4 className="font-semibold">{t('apiReference.requestBodyTitle')}</h4>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(endpoint.example.request, `${endpoint.id}-request`)}
                          >
                            {copiedCode === `${endpoint.id}-request` ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                          </Button>
                        </div>
                        <pre className="bg-muted p-3 rounded-lg text-sm overflow-x-auto">
                          <code>{endpoint.example.request}</code>
                        </pre>
                      </div>
                    )}

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <h4 className="font-semibold">{t('apiReference.responseBody')}</h4>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(endpoint.example.response, `${endpoint.id}-response`)}
                        >
                          {copiedCode === `${endpoint.id}-response` ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        </Button>
                      </div>
                      <pre className="bg-muted p-3 rounded-lg text-sm overflow-x-auto">
                        <code>{endpoint.example.response}</code>
                      </pre>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Webhooks */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Badge variant="outline">POST</Badge>
              {t('apiReference.webhooks')}
            </CardTitle>
            <CardDescription>
              {t('apiReference.webhooksDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <h4 className="font-semibold mb-2">{t('apiReference.howWebhooksWork')}</h4>
              <p className="text-sm text-muted-foreground mb-3">
                {t('apiReference.webhookExplanation')}
              </p>
              <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1 ml-2">
                <li><strong>{t('apiReference.success')}</strong> — {t('apiReference.webhookSuccessItem')}</li>
                <li><strong>{t('apiReference.failure')}</strong> — {t('apiReference.webhookFailureItem')}</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-2">{t('apiReference.webhookDetails')}</h4>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell className="font-medium">{t('apiReference.method')}</TableCell>
                    <TableCell><Badge variant="default">POST</Badge></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">{t('apiReference.contentType')}</TableCell>
                    <TableCell><code>application/json</code></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">{t('apiReference.trigger')}</TableCell>
                    <TableCell>{t('apiReference.triggerDescription')}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">{t('apiReference.expectedResponse')}</TableCell>
                    <TableCell>{t('apiReference.expectedResponseDescription')}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">{t('apiReference.retries')}</TableCell>
                    <TableCell>{t('apiReference.retriesDescription')}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold">{t('apiReference.successPayload')}</h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(webhookSuccessPayload, 'webhook-success')}
                >
                  {copiedCode === 'webhook-success' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">
                {t('apiReference.successPayloadDescription')}
              </p>
              <pre className="bg-muted p-3 rounded-lg text-sm overflow-x-auto">
                <code>{webhookSuccessPayload}</code>
              </pre>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold">{t('apiReference.failurePayload')}</h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(webhookFailurePayload, 'webhook-failure')}
                >
                  {copiedCode === 'webhook-failure' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">
                {t('apiReference.failurePayloadDescription')}
              </p>
              <pre className="bg-muted p-3 rounded-lg text-sm overflow-x-auto">
                <code>{webhookFailurePayload}</code>
              </pre>
            </div>

            <div>
              <h4 className="font-semibold mb-2">{t('apiReference.retryBehavior')}</h4>
              <p className="text-sm text-muted-foreground">
                {t('apiReference.retryBehaviorDescription')}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Data Models */}
        <Card>
          <CardHeader>
            <CardTitle>{t('apiReference.dataModels')}</CardTitle>
            <CardDescription>
              {t('apiReference.dataModelsDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {schemas.map((schema) => (
              <div key={schema.name}>
                <h3 className="font-semibold text-lg mb-2">{schema.name}</h3>
                <p className="text-sm text-muted-foreground mb-3">{schema.description}</p>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('apiReference.property')}</TableHead>
                      <TableHead>{t('apiReference.type')}</TableHead>
                      <TableHead>{t('apiReference.description')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Object.entries(schema.properties).map(([key, value]) => (
                      <TableRow key={key}>
                        <TableCell><code>{key}</code></TableCell>
                        <TableCell><Badge variant="outline">{value.type}</Badge></TableCell>
                        <TableCell className="text-sm">{value.description}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default ApiReference;
